import fp from "lodash/fp";

export type EnumValue = number | string | null;

type CustomEnumItem = {
	defaultSeverity?: number | string | null;
	isTopLevelSecurity?: boolean;
	parent?: any;
};

export type EnumItem = {
	name: string;
	displayName: string;
	value: EnumValue;
	error?: string;
} & CustomEnumItem;

type OptionsParams = {
	defaultOption?: EnumItem;
	predicate?: (i: EnumItem) => boolean;
};

export type CustomEnumType = {
	getDisplayNames(): string[];
	getByValue(value?: EnumValue): EnumItem | undefined;
	getByName(name?: string): EnumItem | undefined;
	getOptions(params?: OptionsParams): EnumItem[];
	getDisplayName(value?: EnumValue): string | undefined;
};

export const createCustomEnum = <T>(optionsDict: {
	[K in keyof T]: EnumItem;
}): typeof optionsDict & CustomEnumType => ({
	...optionsDict,
	getOptions({ defaultOption, predicate } = {}) {
		const result = fp.flow(
			Object.entries,
			fp.map(([_, v]) => v),
			fp.filter(predicate ?? fp.T),
			options => (defaultOption ? [defaultOption, ...options] : options),
		)(optionsDict);

		return result;
	},
	getDisplayNames() {
		return this.getOptions().map(m => m.displayName);
	},
	getByValue(value?: EnumValue) {
		return this.getOptions().find(({ value: v }) => v === value);
	},
	getByName(name?: string) {
		return this.getOptions().find(({ name: n }) => n === name);
	},
	getDisplayName(value?: EnumValue) {
		return this.getByValue(value)?.displayName;
	},
});

// Old types & implementation

export type EnumName = string;

export type EnumMember = {
	name: string;
	displayName: string;
	value: EnumValue;
};

export type SpreadEnum<TKeys extends string> = {
	[keys in TKeys]: EnumValue;
};

export type CustomEnumBase = {
	getDisplayNames(predicate?: () => boolean): string[];
	getByValue(value?: EnumValue): EnumMember | undefined;
	getByName(name?: EnumName): EnumMember | undefined;
	getDDLOptions({ defaultOption, predicate }): any[];
};

export type DDLOptions = {
	defaultOption?: any;
	predicate: (member: EnumMember) => boolean;
};

const defaultOptions: DDLOptions = {
	defaultOption: undefined,
	predicate: _member => true,
};

/**
 * @deprecated use `createCustomEnum`
 */
export class CustomEnum implements CustomEnumBase {
	items: EnumMember[];

	constructor(items: EnumMember[]) {
		this.items = items;

		for (const [key, value] of this.spreadItems()) {
			this[key] = value;
		}
	}

	spreadItems() {
		const dict: Record<string, EnumValue> = {};

		for (const i of this.items) {
			dict[i.name] = i.value;
		}

		return Object.entries(dict);
	}

	getDisplayNames(predicate = defaultOptions.predicate) {
		return this.items.filter(predicate).map(m => m.displayName);
	}

	getByValue(value?: EnumValue) {
		return this.items.find(({ value: v }) => v === value);
	}

	getByName(name?: EnumName) {
		return this.items.find(({ name: n }) => n === name);
	}

	getDisplayName(value?: EnumValue) {
		return this.getByValue(value)?.displayName || "";
	}

	getDDLOptions({ defaultOption, predicate } = defaultOptions) {
		let result = [...this.items];

		if (defaultOption) {
			result.unshift(defaultOption);
		}

		return result.filter(predicate);
	}
}
